import { atom, type WritableAtom } from "jotai";
import { atomWithHash } from "jotai-location";
import type { RESET } from "jotai/vanilla/utils";

import type { AspectType } from "./types.ts";

// explicitly export jotai types so we can export atom hooks directly.
export type * from "jotai";

export const DEFAULT_ASPECT_TYPE = "properties" satisfies AspectType;

const SELECTED_NODE_ID_KEY = "n";
const SELECTED_ASPECT_KEY = "a";
const SELECTED_RESPONSE_ID_KEY = "r";

const atomWithHashOptions: Parameters<typeof atomWithHash<any>>[2] = {
  setHash: "replaceState", // replaceState so that the browser back button works as expected.
};

// Need to redeclare with) the jotai-location result types (or at least, compatible with tem)
// to fix "cannot be named" error since it doesn't export them. Sigh.

export type SetStateActionWithReset<Value> = Value | typeof RESET | ((prev: Value) => Value | typeof RESET);

export type ResettableAtom<Value> = WritableAtom<
  Value,
  [SetStateActionWithReset<Value> /* , AtomWithHashSetOptions? */],
  void
>;

export const selectedNodeIdAtom: ResettableAtom<number | undefined> = atomWithHash(
  SELECTED_NODE_ID_KEY,
  undefined,
  atomWithHashOptions,
);

export const selectedAspectAtom: ResettableAtom<AspectType> = atomWithHash(
  SELECTED_ASPECT_KEY,
  DEFAULT_ASPECT_TYPE,
  atomWithHashOptions,
);

export const selectedResponseIdAtom: ResettableAtom<number | undefined> = atomWithHash(
  SELECTED_RESPONSE_ID_KEY,
  undefined,
  atomWithHashOptions,
);

/** A node to select once the route reaches `path`. See `useFollowNodeLink`. */
export interface PendingSelection {
  nodeId: number;
  aspect: AspectType;
  path: string;
}

/**
 * Selection requested for after a route change. Not in the hash: `navigate` drops it, and this
 * is in flight for one navigation only. The route resetter applies and clears it.
 */
export const pendingSelectionAtom = atom<PendingSelection | undefined>(undefined);
